/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Central role & capability model for eDiagnostics: role groups, their capabilities, and view-as presets. */

/** JWT `roles` claim values (Entra group names) understood by the app. */
export const ROLES = {
  /** GM service team members — core diagnostics access (CCA). */
  service: 'ediag_gm_service',
  /** GM dealer team members — external dealer access (CCA). */
  dealer: 'ediag_gm_dealers',
  /** May view GM service usage analytics. */
  analyticsService: 'ediag_analytics_service',
  /** May view dealer usage analytics. */
  analyticsDealer: 'ediag_analytics_dealer',
  /** Admin console (manage dealer access, data refresh, feedback, exclusions, integrations). */
  admin: 'ediag_admin',
  /** Developer features (API docs, data refresh). */
  dev: 'ediag_dev',
} as const;

export type EdiagRole = (typeof ROLES)[keyof typeof ROLES];

/** Older dealer group kept for backward compatibility with older tokens. */
export const LEGACY_DEALER_ROLE = 'external_dealers';

/**
 * Broad dealer-network group. GM staff frequently hold it alongside an internal group, so it only
 * marks a true dealer account when {@link hasInternalRole} is false.
 */
export const DEALER_ALL_USERS_ROLE = 'Dealer_All_Users';

/** Older (plural) dealer-analytics group still accepted on older tokens; aliases {@link ROLES.analyticsDealer}. */
export const LEGACY_ANALYTICS_DEALER_ROLE = 'ediag_analytics_dealers';

/** Every internal (non-dealer) group — the basis of the superuser persona. */
export const ALL_INTERNAL_ROLES: EdiagRole[] = [
  ROLES.service,
  ROLES.analyticsService,
  ROLES.analyticsDealer,
  ROLES.admin,
  ROLES.dev,
];

/** Fine-grained things a user can do, gated per route / feature / control. */
export type Capability =
  // Analytics page + its audience segments.
  | 'analytics.view'
  | 'analytics.service'
  | 'analytics.dealer'
  // Admin console + individual admin features.
  | 'admin.console'
  | 'admin.dealerAccess'
  | 'admin.dataRefresh'
  | 'admin.feedback'
  | 'admin.exclusions'
  | 'admin.integrations'
  | 'admin.layout'
  // Developer features (also surfaced inside the admin console).
  | 'dev.apiDocs';

/** Direct capabilities granted by each group; aggregate caps are derived in {@link capabilitiesFor}. */
const ROLE_CAPABILITIES: Partial<Record<string, Capability[]>> = {
  // Core diagnostics only: schematics + MECL. No analytics, no admin console.
  [ROLES.service]: [],
  [ROLES.dealer]: [],
  // Analytics visibility comes exclusively from the analytics groups, per audience.
  [ROLES.analyticsService]: ['analytics.service'],
  [ROLES.analyticsDealer]: ['analytics.dealer'],
  [LEGACY_ANALYTICS_DEALER_ROLE]: ['analytics.dealer'],
  // Admin sees everything: every analytics audience plus the full admin console.
  [ROLES.admin]: [
    'analytics.service',
    'analytics.dealer',
    'admin.dealerAccess',
    'admin.dataRefresh',
    'admin.feedback',
    'admin.exclusions',
    'admin.integrations',
    'admin.layout',
  ],
  [ROLES.dev]: ['dev.apiDocs', 'admin.dataRefresh'],
};

/** Any capability that lives under the /admin console (drives the admin nav + route guard). */
const ADMIN_CONSOLE_CAPS: Capability[] = [
  'admin.dealerAccess',
  'admin.dataRefresh',
  'admin.feedback',
  'admin.exclusions',
  'admin.integrations',
  'admin.layout',
  'dev.apiDocs',
];

/** Resolve the full capability set for a list of role strings (unknown roles are ignored). */
export function capabilitiesFor(roles: readonly string[]): Set<Capability> {
  const caps = new Set<Capability>();
  for (const role of roles) {
    for (const cap of ROLE_CAPABILITIES[role] ?? []) caps.add(cap);
  }
  // Derive aggregate capabilities: any analytics audience grants the analytics page.
  if (caps.has('analytics.service') || caps.has('analytics.dealer')) {
    caps.add('analytics.view');
  }
  if (ADMIN_CONSOLE_CAPS.some((c) => caps.has(c))) {
    caps.add('admin.console');
  }
  return caps;
}

/** True when a role string denotes an external dealer partner (current, older, or network group). */
export function isDealerRole(role: string): boolean {
  return role === ROLES.dealer || role === LEGACY_DEALER_ROLE || role === DEALER_ALL_USERS_ROLE;
}

/** Grants {@link ROLES.service} to every authenticated non-dealer so core diagnostics stay open; dealers unchanged. */
export function applyServiceBaseline(roles: readonly string[], isDealer: boolean): string[] {
  if (isDealer) return [...roles];
  return roles.includes(ROLES.service) ? [...roles] : [...roles, ROLES.service];
}

/** Every internal (GM, non-dealer) group the app recognizes, including older groups on older tokens. */
const INTERNAL_ROLE_SET = new Set<string>([
  ...ALL_INTERNAL_ROLES,
  LEGACY_ANALYTICS_DEALER_ROLE,
  'ediag_read',
  'ediag_write',
]);

/** True when a role string denotes an internal GM (non-dealer) group. */
export function isInternalRole(role: string): boolean {
  return INTERNAL_ROLE_SET.has(role);
}

/** True when the role list contains at least one internal GM group (internal wins over any dealer role). */
export function hasInternalRole(roles: readonly string[]): boolean {
  return roles.some(isInternalRole);
}

/** A user holds every internal capability the app exposes (an eDiagnostics superuser). */
export function isSuperuserRoles(roles: readonly string[]): boolean {
  const caps = capabilitiesFor(roles);
  return caps.has('admin.console') && caps.has('analytics.view') && caps.has('dev.apiDocs');
}

/** A "view as" persona for the header role switcher. */
export interface RolePreset {
  id: string;
  labelKey: string;
  descriptionKey: string;
  /** Effective role set applied when this persona is selected. */
  roles: string[];
}

/** Preview personas offered by the header role switcher. */
export const ROLE_PRESETS: RolePreset[] = [
  {
    id: 'service',
    labelKey: 'roles.presets.service.label',
    descriptionKey: 'roles.presets.service.description',
    roles: [ROLES.service],
  },
  {
    id: 'dealer',
    labelKey: 'roles.presets.dealer.label',
    descriptionKey: 'roles.presets.dealer.description',
    roles: [ROLES.dealer],
  },
  {
    id: 'serviceAnalytics',
    labelKey: 'roles.presets.serviceAnalytics.label',
    descriptionKey: 'roles.presets.serviceAnalytics.description',
    roles: [ROLES.service, ROLES.analyticsService],
  },
  {
    id: 'dealerAnalytics',
    labelKey: 'roles.presets.dealerAnalytics.label',
    descriptionKey: 'roles.presets.dealerAnalytics.description',
    roles: [ROLES.service, ROLES.analyticsDealer],
  },
  {
    id: 'admin',
    labelKey: 'roles.presets.admin.label',
    descriptionKey: 'roles.presets.admin.description',
    roles: [ROLES.admin],
  },
  {
    id: 'dev',
    labelKey: 'roles.presets.dev.label',
    descriptionKey: 'roles.presets.dev.description',
    roles: [ROLES.dev],
  },
];

/** Preset by id, or undefined. */
export function presetFor(id: string | null | undefined): RolePreset | undefined {
  return ROLE_PRESETS.find((p) => p.id === id);
}
